import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from './uiTheme';
import { dropdownPortalPosition, stopModalScrollLockPropagation } from './dropdownPortal';
import DropdownSearchPortalLayer from './DropdownSearchPortalLayer';

type SelectOption = {
  value: string;
  label: string;
};

type SelectDropdownProps = {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  compact?: boolean;
  /** Strip own border/padding for use inside InsetLabelField. */
  inset?: boolean;
  ariaLabel?: string;
};

/**
 * Single-select dropdown with portaled list styling aligned to MultiSelectDropdownSearch.
 */
export default function SelectDropdown({
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  id,
  className = '',
  compact = false,
  inset = false,
  ariaLabel,
}: SelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuRect, setMenuRect] = useState<ReturnType<typeof dropdownPortalPosition> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((o) => o.value === value) ?? null;
  const displayLabel = selectedOption?.label ?? placeholder;

  const updateMenuRect = useCallback(() => {
    if (!open || !wrapperRef.current) return;
    setMenuRect(dropdownPortalPosition(wrapperRef.current.getBoundingClientRect()));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setHighlightIndex(0);
    } else {
      const selectedIndex = options.findIndex((o) => o.value === value);
      setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
      buttonRef.current?.focus();
    }
  }, [open, options, value]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }
    updateMenuRect();
    window.addEventListener('scroll', updateMenuRect, true);
    window.addEventListener('resize', updateMenuRect);
    return () => {
      window.removeEventListener('scroll', updateMenuRect, true);
      window.removeEventListener('resize', updateMenuRect);
    };
  }, [open, updateMenuRect]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-highlighted="true"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [open]);

  const handleSelect = (option: SelectOption) => {
    onChange(option.value);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < options.length - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : options.length - 1));
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const option = options[highlightIndex];
      if (option) handleSelect(option);
      return;
    }
  };

  const controlClass = cn(
    'flex w-full items-center justify-between bg-dark-surface transition-colors',
    inset
      ? 'min-h-0 rounded-none border-0 bg-transparent px-3 pb-2 pt-5 shadow-none ring-0'
      : cn(
          'border border-dark-border',
          compact ? 'min-h-[36px] rounded-lg px-2.5' : 'min-h-[42px] rounded-xl px-3',
          open ? 'ring-2 ring-vivid-red/50 border-vivid-red' : '',
          !disabled && 'hover:border-dark-border-glow'
        ),
    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
  );

  const itemPad = compact ? 'px-3 py-2 text-sm' : 'px-4 py-2.5 text-sm';

  return (
    <div className={cn('relative min-w-0', className)} ref={wrapperRef}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id ? `${id}-listbox` : undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className={controlClass}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-left',
            compact ? 'text-sm' : 'text-sm',
            selectedOption ? 'text-slate-100' : 'text-slate-400'
          )}
        >
          {displayLabel}
        </span>
        <ChevronDown
          className={cn(
            'ml-2 shrink-0 text-slate-400 transition-transform pointer-events-none',
            compact ? 'h-3.5 w-3.5' : 'h-4 w-4',
            open && 'rotate-180'
          )}
          aria-hidden
        />
      </button>

      {menuRect &&
        typeof document !== 'undefined' &&
        createPortal(
          <DropdownSearchPortalLayer style={menuRect}>
            <AnimatePresence initial={false}>
              {open ? (
                <motion.ul
                  id={id ? `${id}-listbox` : undefined}
                  ref={listRef}
                  role="listbox"
                  className="overflow-y-auto overscroll-contain rounded-xl border border-dark-border bg-dark-surface py-1 shadow-lg"
                  style={{ maxHeight: menuRect.maxHeight ?? 240 }}
                  onWheel={stopModalScrollLockPropagation}
                  onTouchMove={stopModalScrollLockPropagation}
                  initial={{ opacity: 0, scale: 0.95, y: -6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -6 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                >
                  {options.map((option, i) => {
                    const isSelected = option.value === value;
                    const isHighlighted = i === highlightIndex;
                    return (
                      <li key={option.value || '__empty__'} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          data-highlighted={isHighlighted}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelect(option);
                          }}
                          onMouseEnter={() => setHighlightIndex(i)}
                          className={cn(
                            'w-full cursor-pointer text-left transition-colors',
                            itemPad,
                            isHighlighted || isSelected
                              ? 'bg-vivid-red/20 text-vivid-red'
                              : 'text-slate-100 hover:bg-dark-elevated'
                          )}
                        >
                          <span className="block truncate font-medium">{option.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </motion.ul>
              ) : null}
            </AnimatePresence>
          </DropdownSearchPortalLayer>,
          document.body
        )}
    </div>
  );
}
